import { useEffect, useState, useCallback, useRef } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState } from "@/lib/swr";
import { useDash } from "./ctx";
import { V, useVocab } from "@/lib/vocab";
import { toast } from "sonner";
import { AudiencePicker, AudienceChips } from "@/components/AudiencePicker";
import { chipsOf } from "@/lib/audiences";
import {
  Building2, Save, Palette, Home, CalendarClock, Copy,
  ListChecks, Plus, Pencil, Trash2, AlertTriangle, ExternalLink, Loader2, ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { CollapsibleCard } from "@/components/CollapsibleCard";
import { StickySaveBar } from "@/components/StickySaveBar";
import { brandLogoUrl } from "@/components/BrandCard";

const HOA_ASSOC_TYPES = [["", "Not set"], ["hoa", "HOA"], ["coa", "Condo owners association"],
  ["condo", "Condominium"], ["poa", "POA"], ["master", "Master association"], ["other", "Other"]];
const BUSINESS_TYPES = [["", "Not set"], ["llc", "LLC"], ["corp", "Corporation"], ["sole", "Sole proprietorship"],
  ["partnership", "Partnership"], ["other", "Other"]];

const inputCls = "w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30";
const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-sm font-semibold">{label}{hint && <span className="font-normal text-muted-foreground"> — {hint}</span>}</label>
    <div className="mt-1">{children}</div>
  </div>
);
// Every card folds under a chevron; on a phone all but the first start folded so the page is a
// table of contents, not a scroll. The browser remembers what you left open.
const Card = ({ icon, title, blurb, children, testid, open = true, summary }) => (
  <CollapsibleCard id={`profile.${testid}`} icon={icon} title={title} testid={testid} defaultOpen={open} mobileDefaultOpen={testid === "profile-property"} summary={summary}>
    <div className="mt-4 space-y-4">
      {blurb && <p className="text-xs text-muted-foreground -mt-1">{blurb}</p>}
      {children}
    </div>
  </CollapsibleCard>
);

const EMPTY = {
  legal_name: "", assoc_type: "", website: "",
  address: { line1: "", city: "", state: "", zip: "" },
  insurance: { carrier: "", policy_number: "", expires_on: "", agent_name: "", agent_phone: "" },
};

export default function CommunityProfile() {
  const { tenantId, role } = useDash();
  const v = useVocab();
  const readOnly = role === "board_member";
  const isAdmin = ["company_admin", "platform_admin", "super_admin", "onboarding_admin"].includes(role);
  const [data, setData] = useCachedState(`community-profile:${tenantId}`, null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [proc, setProc] = useState(null);          // procedure being edited/created
  const [procBusy, setProcBusy] = useState(false);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const load = useCallback(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/community-profile`)
      .then((r) => {
        setData(r.data);
        if (!dirtyRef.current) {
          const p = r.data.profile || {};
          setForm({
            ...EMPTY, ...p,
            address: { ...EMPTY.address, ...(p.address || {}) },
            insurance: { ...EMPTY.insurance, ...(p.insurance || {}) },
            unit_count: r.data.unit_count || "",
            minutes_saved_per_contact: r.data.minutes_saved_per_contact || "",
          });
        }
      })
      .catch(() => toast.error(`Couldn't load the ${V.lc("org")} profile`));
  }, [tenantId, setData]);
  useEffect(() => { if (tenantId) load(); }, [tenantId, load]);

  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const upd = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const updIn = (group, k, v) => {
    setForm((f) => ({ ...f, [group]: { ...f[group], [k]: v } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ...form,
        unit_count: form.unit_count === "" ? 0 : Number(form.unit_count),
        minutes_saved_per_contact: form.minutes_saved_per_contact === "" || form.minutes_saved_per_contact == null ? 0 : Number(form.minutes_saved_per_contact),
      };
      const r = await api.put(`/tenants/${tenantId}/community-profile`, body);
      setData((d) => ({ ...d, ...r.data }));
      setDirty(false);
      toast.success(`${V.one("org")} profile saved`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };

  const saveProcedure = async () => {
    if (!proc.title.trim()) { toast.error("Give the procedure a name"); return; }
    setProcBusy(true);
    try {
      const body = { kind: "procedure", title: proc.title.trim(), description: proc.description || "", audiences: proc.audiences && proc.audiences.length ? proc.audiences : chipsOf(proc) };
      if (proc.id) await api.put(`/tenants/${tenantId}/knowledge/${proc.id}`, body);
      else await api.post(`/tenants/${tenantId}/knowledge`, body);
      setProc(null);
      toast.success(proc.id ? "Procedure updated" : "Procedure added — your concierge can explain it now");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save that procedure"); }
    finally { setProcBusy(false); }
  };

  const removeProcedure = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? Your concierge will stop explaining it.`)) return;
    try {
      await api.delete(`/tenants/${tenantId}/knowledge/${p.id}`);
      toast.success("Procedure deleted");
      load();
    } catch { toast.error("Couldn't delete that"); }
  };

  if (!data || !form) {
    return <div className="flex items-center gap-2 text-muted-foreground p-6" data-testid="profile-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading {V.lc("org")} profile…</div>;
  }

  const roster = data.roster || {};
  const ob = data.obligations || { items: [] };
  const color = data.brand_color || "#D32F2F";

  return (
    <div className="space-y-6" data-testid="community-profile-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <PageTitle title={<><Building2 className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> {v.mod("profile")}</>} about={<>The facts about {data.name} that everything else reads — your report&apos;s adoption numbers, your
            branded emails and pages, and the answers your concierge gives.</>} />
        </div>
        {!readOnly && (
          <button data-testid="profile-save-btn" disabled={saving} onClick={save}
            className={`rounded-full font-semibold px-5 py-2.5 flex items-center gap-2 transition-colors disabled:opacity-60 ${dirty ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-redline-500 text-white hover:bg-redline-600"}`}>
            <Save className="h-4 w-4" /> {saving ? "Saving…" : dirty ? "Unsaved changes — Save" : "Save"}
          </button>
        )}
      </div>

      {/* PROPERTY — the door count everything else divides by (HOA only; a trades company has no door count) */}
      {v.isHoa && <Card icon={Home} title="Property" testid="profile-property"
        blurb={`Your door count sizes the ${V.lc("org")} for reports and prices per-unit add-ons.`}>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Total properties" hint="doors in the association">
            <input data-testid="profile-unit-count" type="number" min="0" disabled={readOnly}
              value={form.unit_count} onChange={(e) => upd("unit_count", e.target.value)}
              className={inputCls} placeholder="e.g. 300" />
          </Field>
          <div className="md:col-span-2 rounded-xl bg-sand-50 border border-sand-200 p-3.5 text-sm" data-testid="profile-roster-check">
            <div className="font-semibold text-ink">Roster cross-check</div>
            <div className="text-muted-foreground text-xs mt-1">
              Your roster holds <b>{roster.households} {roster.households === 1 ? "property" : "properties"}</b>
              {" "}· <b>{roster.active_homes}</b> heard from in the last 30 days ({roster.active_residents} {roster.active_residents === 1 ? V.lc("person") : V.lcs("person")} texted, called or signed in).
              {roster.unit_count_source !== "manager" && <> No total set yet — reports use the roster count ({roster.households}) as the {V.lc("org")}'s size.</>}
            </div>
            {roster.roster_exceeds && (
              <div className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-amber-700" data-testid="profile-roster-warning">
                <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
                The roster has more properties ({roster.households}) than the total you set. One of the two is wrong —
                worth a look before the next report goes out.
              </div>
            )}
          </div>
        </div>
      </Card>}

      {/* REPORTING — the ROI assumption behind "hours saved" (Administrator-only) */}
      {isAdmin && <Card icon={CalendarClock} title="Reporting" testid="profile-reporting"
        blurb={`How many staff minutes each AI-handled conversation counts as saved — this drives the "hours saved" figure on your reports.`}>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Minutes saved per AI-handled conversation" hint="default 15">
            <input data-testid="profile-minutes-saved" type="number" min="1" max="120" disabled={readOnly}
              value={form.minutes_saved_per_contact} onChange={(e) => upd("minutes_saved_per_contact", e.target.value)}
              className={inputCls} placeholder="15" />
          </Field>
          <div className="md:col-span-2 rounded-xl bg-sand-50 border border-sand-200 p-3.5 text-sm" data-testid="profile-minutes-note">
            <div className="text-muted-foreground text-xs">
              Every conversation the concierge resolves <b>without a person</b> counts as this many minutes of staff time saved
              on your community and portfolio reports. Leave blank to use the default of <b>15 minutes</b>. Applies to {data.name} only.
            </div>
          </div>
        </div>
      </Card>}

      {/* IDENTITY */}
      <Card icon={ListChecks} title="Identity" testid="profile-identity"
        blurb={`Used on legal notices, the public page and anywhere the concierge is asked who the ${v.isHoa ? "association" : "business"} actually is.`}>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label={v.isHoa ? "Legal association name" : "Legal business name"} hint="as registered">
            <input data-testid="profile-legal-name" disabled={readOnly} value={form.legal_name}
              onChange={(e) => upd("legal_name", e.target.value)} className={inputCls}
              placeholder={V.isHoa ? `Maplewood ${V.one("org")} ${V.many("person")} Association, Inc.` : "Acme Roofing LLC"} />
          </Field>
          <Field label={V.isHoa ? "Association type" : "Business type"}>
            <select data-testid="profile-assoc-type" disabled={readOnly} value={form.assoc_type}
              onChange={(e) => upd("assoc_type", e.target.value)} className={inputCls}>
              {(V.isHoa ? HOA_ASSOC_TYPES : BUSINESS_TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <Field label={v.isHoa ? "Property address" : "Office address"}>
              <input data-testid="profile-addr-line1" disabled={readOnly} value={form.address.line1}
                onChange={(e) => updIn("address", "line1", e.target.value)} className={inputCls} placeholder="1200 Maplewood Dr" />
            </Field>
          </div>
          <Field label="City">
            <input data-testid="profile-addr-city" disabled={readOnly} value={form.address.city}
              onChange={(e) => updIn("address", "city", e.target.value)} className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="State">
              <input data-testid="profile-addr-state" disabled={readOnly} value={form.address.state}
                onChange={(e) => updIn("address", "state", e.target.value)} className={inputCls} placeholder="TX" />
            </Field>
            <Field label="ZIP">
              <input data-testid="profile-addr-zip" disabled={readOnly} value={form.address.zip}
                onChange={(e) => updIn("address", "zip", e.target.value)} className={inputCls} />
            </Field>
          </div>
        </div>
        <Field label="Website" hint="optional">
          <input data-testid="profile-website" disabled={readOnly} value={form.website}
            onChange={(e) => upd("website", e.target.value)} className={inputCls} placeholder="https://…" />
        </Field>
        {data?.mail_address && (
          <Field label="Mail address" hint="forward emails here · TownSq notification email">
            <div className="flex items-center gap-2" data-testid="profile-mail-address">
              <code className="flex-1 min-w-0 rounded-xl bg-sand-50 border border-sand-200 px-3 py-2.5 text-sm break-all">{data.mail_address}</code>
              <button type="button" data-testid="profile-mail-copy" title="Copy" onClick={() => navigator.clipboard?.writeText(data.mail_address).then(() => toast.success("Copied"))}
                className="h-10 w-10 rounded-xl border border-sand-200 flex items-center justify-center hover:border-ink shrink-0"><Copy className="h-4 w-4" /></button>
              <Link to="/dashboard/mail" data-testid="profile-mail-open" className="text-xs font-bold text-redline-600 hover:text-redline-700 shrink-0">Open Mail →</Link>
            </div>
          </Field>
        )}
      </Card>

      {/* BRAND — moved to Settings → Channels & Experience (iter377); this is just the pointer */}
      <div className="rounded-2xl border border-sand-200 bg-white p-4 md:px-5 flex items-center gap-3 flex-wrap" data-testid="profile-brand-pointer">
        {data.has_logo ? (
          <img src={brandLogoUrl(tenantId, data.logo_ver)} alt="" data-testid="profile-brand-logo-thumb"
            className="h-10 w-10 object-contain rounded-lg border border-sand-200 bg-white p-1 shrink-0" />
        ) : (
          <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0 text-white font-bold" style={{ backgroundColor: color }} data-testid="profile-brand-initial">
            {(data.name || "?").trim().charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-[12rem] flex items-center gap-2 text-sm">
          <Palette className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="text-ink">Logo &amp; colour live in <span className="font-semibold">Settings → Channels &amp; Experience</span></span>
          <span className="h-3.5 w-3.5 rounded-full border border-sand-200 shrink-0" style={{ backgroundColor: color }} data-testid="profile-brand-swatch" />
        </div>
        <Link to="/dashboard/settings?tab=channels" data-testid="profile-brand-link"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-redline-600 hover:text-redline-700 shrink-0">
          Open brand settings <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* PROPERTY PROCEDURES — stored as concierge knowledge (HOA only; trades teach these as Quick facts) */}
      {v.isHoa && <Card icon={ListChecks} title="Property procedures" testid="profile-procedures"
        blurb="How things actually get done here — replacing a pool fob, reporting a leak after hours, booking a move-in. Your concierge answers from these word for word.">
        {(data.procedures || []).length === 0 && !proc && (
          <p className="text-sm text-muted-foreground" data-testid="profile-procedures-empty">
            Nothing written down yet. Add the three things {V.lcs("person")} ask about most and the concierge stops guessing.
          </p>
        )}
        <div className="space-y-2">
          {(data.procedures || []).map((p) => (
            <div key={p.id} className="rounded-xl border border-sand-200 p-3.5" data-testid={`procedure-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm text-ink">{p.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">{p.description}</div>
                  <div className="mt-1.5"><AudienceChips tenantId={tenantId} item={p} /></div>
                </div>
                {!readOnly && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button data-testid={`procedure-edit-${p.id}`} onClick={() => setProc({ ...p })}
                      className="h-8 w-8 rounded-lg hover:bg-sand-200 text-muted-foreground hover:text-ink flex items-center justify-center transition-colors">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`procedure-delete-${p.id}`} onClick={() => removeProcedure(p)}
                      className="h-8 w-8 rounded-lg hover:bg-rose-50 text-muted-foreground hover:text-rose-600 flex items-center justify-center transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {proc ? (
          <div className="rounded-xl border-2 border-redline-200 bg-sand-50 p-4 space-y-3" data-testid="procedure-editor">
            <Field label="What is this procedure?">
              <input data-testid="procedure-title" value={proc.title || ""} autoFocus
                onChange={(e) => setProc((p) => ({ ...p, title: e.target.value }))}
                className={inputCls} placeholder="Replacing a lost pool key fob" />
            </Field>
            <Field label="The steps" hint={`write it the way you'd say it to ${V.a("person")}`}>
              <textarea data-testid="procedure-description" rows={4} value={proc.description || ""}
                onChange={(e) => setProc((p) => ({ ...p, description: e.target.value }))}
                className={`${inputCls} resize-y`}
                placeholder="Email the office with your address. Replacements are $35, charged to your account, programmed within two business days…" />
            </Field>
            <Field label="Who can the AI share this with?">
              <div data-testid="procedure-audience">
                <AudiencePicker tenantId={tenantId} testid="procedure-audience" audiences={proc.audiences || chipsOf(proc)}
                  onAudiences={(list) => setProc((p) => ({ ...p, audiences: list }))} />
              </div>
            </Field>
            <div className="flex items-center gap-2">
              <button data-testid="procedure-save" disabled={procBusy} onClick={saveProcedure}
                className="rounded-full bg-redline-500 text-white font-semibold px-4 py-2 text-sm hover:bg-redline-600 disabled:opacity-60">
                {procBusy ? "Saving…" : proc.id ? "Update procedure" : "Add procedure"}
              </button>
              <button data-testid="procedure-cancel" onClick={() => setProc(null)}
                className="text-sm font-semibold text-muted-foreground hover:text-ink">Cancel</button>
            </div>
          </div>
        ) : !readOnly && (
          <button data-testid="procedure-add" onClick={() => setProc({ title: "", description: "", audiences: ["anyone"] })}
            className="rounded-full border border-sand-200 bg-white px-4 py-2 text-xs font-bold text-ink hover:border-ink transition-colors flex items-center gap-1.5">
            <Plus className="h-4 w-4" /> Add a procedure
          </button>
        )}
      </Card>}

      {/* OBLIGATIONS STRIP — the register lives on its own page */}
      <Card icon={CalendarClock} title="Upcoming obligations" testid="profile-obligations"
        blurb={`Insurance, permits, inspections and filings you're tracking in ${v.mod("renewals")}.`}>
        {ob.items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="profile-obligations-empty">
            Nothing due in the next 90 days.
          </p>
        ) : (
          <div className="space-y-1.5">
            {ob.items.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`profile-obligation-${i.id}`}>
                <span className="truncate">{i.title}</span>
                <span className={`text-xs font-bold shrink-0 ${i.status === "overdue" ? "text-rose-600" : i.status === "due_soon" ? "text-amber-600" : "text-muted-foreground"}`}>
                  {i.status === "overdue" ? `overdue · was due ${i.due_on}` : `${i.due_on} · in ${i.days_out} day${i.days_out === 1 ? "" : "s"}`}
                </span>
              </div>
            ))}
          </div>
        )}
        <Link to="/dashboard/renewals" data-testid="profile-open-renewals"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-redline-600 hover:text-redline-700">
          Open {v.mod("renewals")} <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </Card>
      {!readOnly && <StickySaveBar dirty={dirty} saving={saving} onSave={save} label="Profile changed" testid="profile-sticky" />}
      {dirty && <div className="h-20" aria-hidden="true" />}
    </div>
  );
}
